// Opschoon-paneel (v18.4 round 2, 2.7) — alleen voor admins.
// Sluit checklists af die niemand meer gaat invullen. Er wordt NOOIT iets verwijderd:
// alles krijgt de eindstatus "Gemist" met reden, ingediende checklists blijven staan.
import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, History, Info, Sparkles, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { API_V2, hdrs } from '@/components/checklists/checklistUtils';

const LABELS = {
  vervangen: 'Vervangen door nieuwe periode',
  dubbel: 'Dubbel exemplaar',
  locatie_nvt: 'Locatie niet van toepassing',
  persoon_nvt: 'Persoon niet (meer) van toepassing',
  template_inactief: 'Template niet actief',
  ouder_dan_30_dagen: 'Ouder dan 30 dagen',
};

const fmtDatum = (d) => d
  ? new Date(d).toLocaleDateString('nl-NL', { day: '2-digit', month: 'short', year: 'numeric' })
  : null;

export const TabOpschonen = ({ onUitgevoerd }) => {
  const [bezig, setBezig] = useState(false);
  const [resultaat, setResultaat] = useState(null);
  const [uitgevoerd, setUitgevoerd] = useState(false);
  const [bevestigen, setBevestigen] = useState(false);
  const [status, setStatus] = useState(null);

  const laadStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API_V2}/checklists/opschoon-status`, { headers: hdrs() });
      if (res.ok) setStatus(await res.json());
    } catch { /* banner is informatief — stilte is beter dan een toast */ }
  }, []);

  useEffect(() => { laadStatus(); }, [laadStatus]);

  const roep = async (body) => {
    setBezig(true);
    try {
      const res = await fetch(`${API_V2}/checklists/opschonen`, {
        method: 'POST', headers: { ...hdrs(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || 'Opschonen mislukt'); return null; }
      return data;
    } catch (e) {
      toast.error(`Netwerkfout: ${e.message}`);
      return null;
    } finally { setBezig(false); }
  };

  const proefrun = async () => {
    setResultaat(null);
    setUitgevoerd(false);
    const data = await roep({ dry_run: true });
    if (data) {
      setResultaat(data);
      toast.success(data.totaal === 0
        ? 'Proefrun klaar — niets meer op te schonen'
        : `Proefrun klaar — ${data.totaal} checklist${data.totaal === 1 ? '' : 's'} zou worden afgesloten`);
    }
  };

  const uitvoeren = async () => {
    setBevestigen(false);
    const data = await roep({ dry_run: false, bevestig: true });
    if (data) {
      setUitgevoerd(true);
      setResultaat(data);
      toast.success('Opschonen uitgevoerd');
      laadStatus();
      onUitgevoerd?.();
    }
  };

  const categorieen = resultaat?.categorieen || {};
  const totaal = resultaat?.totaal ?? 0;
  const logboek = status?.logboek || [];

  return (
    <div className="space-y-4" data-testid="tab-opschonen">
      {status && status.opschoning_uitgevoerd === false && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3"
          data-testid="opschonen-banner">
          <Info className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-amber-900">
            <p className="font-medium">
              De eenmalige opschoning is nog niet uitgevoerd — automatisch afsluiten staat uit tot je dit doet.
            </p>
            <p className="mt-1 text-amber-800">
              Tot dan wordt niets automatisch op <strong>Gemist</strong> gezet.
              {status.dubbele_slots?.extra_documenten > 0 && (
                <> Er staan {status.dubbele_slots.extra_documenten} dubbele openstaande
                checklist{status.dubbele_slots.extra_documenten === 1 ? '' : 's'} klaar; de unieke
                index wordt pas gebouwd zodra die zijn afgesloten.</>
              )}
            </p>
          </div>
        </div>
      )}
      {status && (
        <div className="bg-white rounded-xl border border-slate-200 px-5 py-3 text-sm"
          data-testid="index-status">
          <span className="text-slate-500">Unieke beveiliging tegen dubbele checklists: </span>
          {status.unique_index_ok ? (
            <span className="font-medium text-emerald-600">actief</span>
          ) : (
            <span className="font-medium text-amber-600">
              nog niet actief — voer eerst de opschoning uit
            </span>
          )}
          {status.dubbele_slots?.extra_documenten > 0 && (
            <span className="text-slate-500">
              {' '}({status.dubbele_slots.extra_documenten} dubbele openstaande checklist
              {status.dubbele_slots.extra_documenten === 1 ? '' : 's'} te gaan)
            </span>
          )}
        </div>
      )}
      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
        <div className="flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-[#1f2548] mt-0.5" />
          <div className="flex-1">
            <h3 className="font-semibold text-[#1f2548]">Checklists opschonen</h3>
            <p className="text-sm text-slate-500 mt-1">
              Sluit openstaande checklists af die niemand meer gaat invullen. Ze krijgen de
              eindstatus <strong>Gemist</strong> met reden en blijven in Historie zichtbaar.
              Er wordt niets verwijderd en ingevulde checklists blijven ongemoeid.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 pt-1">
          <Button onClick={proefrun} disabled={bezig} variant="outline" data-testid="opschonen-proefrun-btn">
            {bezig ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Proefrun
          </Button>
          <Button onClick={() => setBevestigen(true)}
            disabled={bezig || !resultaat || totaal === 0 || resultaat?.uitgevoerd}
            className="bg-[#1f2548] hover:bg-[#2a3060] text-white" data-testid="opschonen-uitvoeren-btn">
            Uitvoeren
          </Button>
          {resultaat?.uitgevoerd && (
            <p className="text-xs text-slate-500 self-center" data-testid="opschonen-nieuwe-proefrun">
              Doe eerst een nieuwe proefrun voordat je opnieuw uitvoert
            </p>
          )}
        </div>
      </div>

      {resultaat && totaal === 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center"
          data-testid="opschonen-leeg">
          <CheckCircle2 className="w-10 h-10 text-green-400 mx-auto mb-2" />
          <p className="text-slate-600 font-medium">Niets meer op te schonen</p>
          <p className="text-sm text-slate-400 mt-1">
            {resultaat.actief_bekeken} openstaande checklists bekeken — alles is nog van toepassing.
          </p>
        </div>
      )}

      {resultaat && totaal > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="opschonen-resultaat">
          <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
            <p className="text-sm font-medium text-slate-700">
              {uitgevoerd ? 'Uitgevoerd' : 'Proefrun'} — {totaal} checklist{totaal === 1 ? '' : 's'}
            </p>
            <Badge variant="outline" className="text-xs">{resultaat.actief_bekeken} actief bekeken</Badge>
          </div>
          <div className="divide-y divide-slate-100">
            {Object.entries(LABELS).map(([sleutel, label]) => {
              const vak = categorieen[sleutel] || { aantal: 0, voorbeelden: [] };
              return (
                <div key={sleutel} className="px-5 py-3" data-testid={`opschonen-categorie-${sleutel}`}>
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-slate-700">{label}</p>
                    <Badge className={vak.aantal > 0 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-400'}>
                      {vak.aantal}
                    </Badge>
                  </div>
                  {vak.voorbeelden?.length > 0 && (
                    <ul className="mt-2 space-y-0.5">
                      {vak.voorbeelden.map(v => (
                        <li key={v.id} className="text-xs text-slate-400"
                          data-testid={`opschonen-voorbeeld-${v.id}`}>
                          {v.locatie} — {v.template}
                          {v.periode_label ? ` — ${v.periode_label}` : ''}
                          {v.persoon ? ` — ${v.persoon}` : ''}
                          {v.deadline ? ` — deadline ${fmtDatum(v.deadline)}` : ''}
                        </li>
                      ))}
                      {vak.aantal > vak.voorbeelden.length && (
                        <li className="text-xs text-slate-300">
                          + {vak.aantal - vak.voorbeelden.length} meer
                        </li>
                      )}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {logboek.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="opschonen-logboek">
          <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
            <History className="w-4 h-4 text-slate-400" />
            <p className="text-sm font-medium text-slate-700">Eerdere opschoningen</p>
          </div>
          <div className="divide-y divide-slate-100">
            {logboek.map(rij => (
              <div key={rij.id} className="px-5 py-3" data-testid={`opschonen-log-${rij.id}`}>
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm text-slate-700">
                    {fmtDatum(rij.uitgevoerd_op)} — {rij.door || 'onbekend'}
                  </p>
                  <Badge variant="outline" className="text-xs">{rij.totaal} afgesloten</Badge>
                </div>
                <p className="text-xs text-slate-400 mt-1">
                  {Object.entries(LABELS)
                    .filter(([sleutel]) => (rij.per_categorie?.[sleutel] || 0) > 0)
                    .map(([sleutel, label]) => `${label}: ${rij.per_categorie[sleutel]}`)
                    .join(' · ') || 'geen categorieën'}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      <AlertDialog open={bevestigen} onOpenChange={setBevestigen}>
        <AlertDialogContent data-testid="opschonen-bevestig-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" /> Opschonen uitvoeren?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {totaal} checklist{totaal === 1 ? ' wordt' : 's worden'} afgesloten als Gemist.
              Er wordt niets verwijderd.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="opschonen-annuleer-btn">Annuleren</AlertDialogCancel>
            <AlertDialogAction onClick={uitvoeren} disabled={bezig}
              className="bg-[#1f2548] hover:bg-[#2a3060]"
              data-testid="opschonen-bevestig-btn">
              {bezig ? 'Bezig...' : 'Uitvoeren'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
